import { useCallback } from 'react';

interface UseOptimisticMutationOptions<TSnapshot, TResult> {
  captureSnapshot: () => TSnapshot;
  applyOptimistic: () => void;
  mutate: () => Promise<TResult>;
  validateResult?: (result: TResult) => void;
  onSuccess?: (result: TResult, snapshot: TSnapshot) => Promise<void> | void;
  rollback: (snapshot: TSnapshot) => void;
  onError?: (error: unknown, snapshot: TSnapshot) => Promise<void> | void;
}

export function useOptimisticMutation() {
  return useCallback(
    async <TSnapshot, TResult>({
      captureSnapshot,
      applyOptimistic,
      mutate,
      validateResult,
      onSuccess,
      rollback,
      onError,
    }: UseOptimisticMutationOptions<TSnapshot, TResult>) => {
      const snapshot = captureSnapshot();
      applyOptimistic();

      try {
        const result = await mutate();
        validateResult?.(result);
        await onSuccess?.(result, snapshot);
        return result;
      } catch (error) {
        rollback(snapshot);
        await onError?.(error, snapshot);
        throw error;
      }
    },
    [],
  );
}
